import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Settings,
  Save,
  RefreshCw,
  Shield,
  Mail,
  Bell,
  Lock,
  Globe,
  Database,
  Server,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'

interface SystemSettings {
  general: {
    siteName: string
    siteUrl: string
    maintenanceMode: boolean
    allowRegistrations: boolean
  }
  email: {
    smtpHost: string
    smtpPort: number
    smtpUser: string
    smtpPassword: string
    fromEmail: string
    fromName: string
  }
  security: {
    requireEmailVerification: boolean
    requireTwoFactor: boolean
    sessionTimeout: number
    maxLoginAttempts: number
  }
  notifications: {
    emailNotifications: boolean
    adminNotifications: boolean
    userNotifications: boolean
  }
  integrations: {
    zillowApiKey: string
    googlePlacesApiKey: string
    stripeApiKey: string
  }
}

export const AdminSettingsPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [settings, setSettings] = useState<SystemSettings>({
    general: {
      siteName: 'Open1031',
      siteUrl: 'https://open1031.com',
      maintenanceMode: false,
      allowRegistrations: true
    },
    email: {
      smtpHost: '',
      smtpPort: 587,
      smtpUser: '',
      smtpPassword: '',
      fromEmail: 'noreply@open1031.com',
      fromName: 'Open1031'
    },
    security: {
      requireEmailVerification: true,
      requireTwoFactor: false,
      sessionTimeout: 3600,
      maxLoginAttempts: 5
    },
    notifications: {
      emailNotifications: true,
      adminNotifications: true,
      userNotifications: true
    },
    integrations: {
      zillowApiKey: '',
      googlePlacesApiKey: '',
      stripeApiKey: ''
    }
  })

  useEffect(() => {
    if (!currentUser?.isAdmin) {
      navigate('/app')
      return
    }
    loadSettings()
  }, [currentUser, navigate])

  const loadSettings = async () => {
    try {
      setIsLoading(true)
      // Mock load - replace with actual API call
      // const response = await adminApi.getSettings()
      // setSettings(response.settings)
      await new Promise(resolve => setTimeout(resolve, 500)) // Simulate API call
    } catch (error) {
      console.error('Failed to load settings:', error)
      toast.error('Failed to load settings')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveSettings = async () => {
    try {
      setIsSaving(true)
      // Mock save - replace with actual API call
      // await adminApi.updateSettings(settings)
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API call
      toast.success('Settings saved successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save settings')
    } finally {
      setIsSaving(false)
    }
  }

  const handleResetSettings = () => {
    if (!confirm('Are you sure you want to reset all settings to default values? This action cannot be undone.')) {
      return
    }
    loadSettings()
    toast.success('Settings reset to default values')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-3">
            <Settings className="h-8 w-8 text-primary-600" />
            System Settings
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Configure system-wide settings and preferences
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleResetSettings} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSaveSettings} disabled={isSaving}>
            {isSaving ? (
              <LoadingSpinner size="sm" variant="icon" className="mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </div>

      {/* General Settings */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="h-5 w-5 text-primary-600" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            General Settings
          </h2>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Site Name
              </label>
              <Input
                value={settings.general.siteName}
                onChange={(e) => setSettings({
                  ...settings,
                  general: { ...settings.general, siteName: e.target.value }
                })}
                placeholder="Open1031"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Site URL
              </label>
              <Input
                value={settings.general.siteUrl}
                onChange={(e) => setSettings({
                  ...settings,
                  general: { ...settings.general, siteUrl: e.target.value }
                })}
                placeholder="https://open1031.com"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.general.maintenanceMode}
                onChange={(e) => setSettings({
                  ...settings,
                  general: { ...settings.general, maintenanceMode: e.target.checked }
                })}
                className="rounded"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Maintenance Mode
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.general.allowRegistrations}
                onChange={(e) => setSettings({
                  ...settings,
                  general: { ...settings.general, allowRegistrations: e.target.checked }
                })}
                className="rounded"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Allow New Registrations
              </span>
            </label>
          </div>
        </div>
      </Card>

      {/* Email Settings */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="h-5 w-5 text-primary-600" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Email Settings
          </h2>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                SMTP Host
              </label>
              <Input
                type="text"
                value={settings.email.smtpHost}
                onChange={(e) => setSettings({
                  ...settings,
                  email: { ...settings.email, smtpHost: e.target.value }
                })}
                placeholder="smtp.example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                SMTP Port
              </label>
              <Input
                type="number"
                value={settings.email.smtpPort}
                onChange={(e) => setSettings({
                  ...settings,
                  email: { ...settings.email, smtpPort: Number(e.target.value) }
                })}
                placeholder="587"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                SMTP User
              </label>
              <Input
                type="text"
                value={settings.email.smtpUser}
                onChange={(e) => setSettings({
                  ...settings,
                  email: { ...settings.email, smtpUser: e.target.value }
                })}
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                SMTP Password
              </label>
              <Input
                type="password"
                value={settings.email.smtpPassword}
                onChange={(e) => setSettings({
                  ...settings,
                  email: { ...settings.email, smtpPassword: e.target.value }
                })}
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                From Email
              </label>
              <Input
                type="email"
                value={settings.email.fromEmail}
                onChange={(e) => setSettings({
                  ...settings,
                  email: { ...settings.email, fromEmail: e.target.value }
                })}
                placeholder="noreply@open1031.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                From Name
              </label>
              <Input
                type="text"
                value={settings.email.fromName}
                onChange={(e) => setSettings({
                  ...settings,
                  email: { ...settings.email, fromName: e.target.value }
                })}
                placeholder="Open1031"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Security Settings */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-5 w-5 text-primary-600" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Security Settings
          </h2>
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.security.requireEmailVerification}
                onChange={(e) => setSettings({
                  ...settings,
                  security: { ...settings.security, requireEmailVerification: e.target.checked }
                })}
                className="rounded"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Require Email Verification
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.security.requireTwoFactor}
                onChange={(e) => setSettings({
                  ...settings,
                  security: { ...settings.security, requireTwoFactor: e.target.checked }
                })}
                className="rounded"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Require Two-Factor Authentication
              </span>
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Session Timeout (seconds)
              </label>
              <Input
                type="number"
                value={settings.security.sessionTimeout}
                onChange={(e) => setSettings({
                  ...settings,
                  security: { ...settings.security, sessionTimeout: Number(e.target.value) }
                })}
                placeholder="3600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Max Login Attempts
              </label>
              <Input
                type="number"
                value={settings.security.maxLoginAttempts}
                onChange={(e) => setSettings({
                  ...settings,
                  security: { ...settings.security, maxLoginAttempts: Number(e.target.value) }
                })}
                placeholder="5"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Notification Settings */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="h-5 w-5 text-primary-600" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Notification Settings
          </h2>
        </div>
        <div className="space-y-4">
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.notifications.emailNotifications}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, emailNotifications: e.target.checked }
                })}
                className="rounded"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Enable Email Notifications
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.notifications.adminNotifications}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, adminNotifications: e.target.checked }
                })}
                className="rounded"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Enable Admin Notifications
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.notifications.userNotifications}
                onChange={(e) => setSettings({
                  ...settings,
                  notifications: { ...settings.notifications, userNotifications: e.target.checked }
                })}
                className="rounded"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Enable User Notifications
              </span>
            </label>
          </div>
        </div>
      </Card>

      {/* Integration Settings */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Server className="h-5 w-5 text-primary-600" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Integration Settings
          </h2>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Zillow API Key
              </label>
              <Input
                type="password"
                value={settings.integrations.zillowApiKey}
                onChange={(e) => setSettings({
                  ...settings,
                  integrations: { ...settings.integrations, zillowApiKey: e.target.value }
                })}
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Google Places API Key
              </label>
              <Input
                type="password"
                value={settings.integrations.googlePlacesApiKey}
                onChange={(e) => setSettings({
                  ...settings,
                  integrations: { ...settings.integrations, googlePlacesApiKey: e.target.value }
                })}
                placeholder="••••••••"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Stripe API Key
              </label>
              <Input
                type="password"
                value={settings.integrations.stripeApiKey}
                onChange={(e) => setSettings({
                  ...settings,
                  integrations: { ...settings.integrations, stripeApiKey: e.target.value }
                })}
                placeholder="••••••••"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Database Settings */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Database className="h-5 w-5 text-primary-600" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Database Settings
          </h2>
        </div>
        <div className="space-y-4">
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                  Database Management
                </h3>
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Database operations should be performed through Cloudflare D1 console or migration scripts.
                  Direct database modifications from this interface are not recommended.
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => toast.info('Database backup functionality would be implemented here')}>
              Backup Database
            </Button>
            <Button variant="outline" onClick={() => toast.info('Database restore functionality would be implemented here')}>
              Restore Database
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}




